const DIGIT_SELECTOR = '.otp-digit'

function getDigitInputs(container: Element): NodeListOf<HTMLInputElement> {
  return container.querySelectorAll<HTMLInputElement>(DIGIT_SELECTOR)
}

function syncToHiddenInput(
  digitInputs: NodeListOf<HTMLInputElement>,
  hiddenInput: HTMLInputElement,
): void {
  const combinedValue = Array.from(digitInputs)
    .map((input) => input.value)
    .join('')

  hiddenInput.value = combinedValue
}

function prefillFromHiddenInput(
  digitInputs: NodeListOf<HTMLInputElement>,
  hiddenInput: HTMLInputElement,
): void {
  const existingValue = hiddenInput.value
  if (!existingValue) return

  for (let i = 0; i < existingValue.length && i < digitInputs.length; i++) {
    digitInputs[i].value = existingValue[i]
  }
}

function sanitizeDigit(value: string): string {
  return value.replace(/[^0-9]/g, '')
}

function handleDigitInput(
  input: HTMLInputElement,
  index: number,
  digitInputs: NodeListOf<HTMLInputElement>,
  hiddenInput: HTMLInputElement,
): void {
  const sanitized = sanitizeDigit(input.value)
  input.value = sanitized

  const hasNextDigit = index < digitInputs.length - 1
  if (sanitized && hasNextDigit) {
    digitInputs[index + 1].focus()
  }

  syncToHiddenInput(digitInputs, hiddenInput)
}

function handleBackspace(
  input: HTMLInputElement,
  index: number,
  digitInputs: NodeListOf<HTMLInputElement>,
  event: KeyboardEvent,
): void {
  const isBackspace = event.key === 'Backspace'
  const isEmpty = !input.value
  const hasPreviousDigit = index > 0

  if (isBackspace && isEmpty && hasPreviousDigit) {
    digitInputs[index - 1].focus()
  }
}

function handlePaste(
  startIndex: number,
  digitInputs: NodeListOf<HTMLInputElement>,
  hiddenInput: HTMLInputElement,
  event: ClipboardEvent,
): void {
  event.preventDefault()

  const pastedText = event.clipboardData?.getData('text') ?? ''
  const sanitizedDigits = sanitizeDigit(pastedText)

  for (let i = 0; i < sanitizedDigits.length; i++) {
    const targetIndex = startIndex + i
    if (targetIndex >= digitInputs.length) break

    digitInputs[targetIndex].value = sanitizedDigits[i]
  }

  const finalFocusIndex = Math.min(
    startIndex + sanitizedDigits.length,
    digitInputs.length - 1,
  )
  digitInputs[finalFocusIndex].focus()

  syncToHiddenInput(digitInputs, hiddenInput)
}

function attachEventListeners(
  digitInputs: NodeListOf<HTMLInputElement>,
  hiddenInput: HTMLInputElement,
): void {
  digitInputs.forEach((input, index) => {
    input.addEventListener('input', () => {
      handleDigitInput(input, index, digitInputs, hiddenInput)
    })

    input.addEventListener('keydown', (event) => {
      handleBackspace(input, index, digitInputs, event)
    })

    input.addEventListener('paste', (event) => {
      handlePaste(index, digitInputs, hiddenInput, event)
    })
  })
}

function initOtpContainer(container: Element): void {
  const targetId = container.getAttribute('data-target-id')
  if (!targetId) return

  const hiddenInput = document.getElementById(targetId) as HTMLInputElement
  if (!hiddenInput) return

  const digitInputs = getDigitInputs(container)
  if (digitInputs.length === 0) return

  prefillFromHiddenInput(digitInputs, hiddenInput)
  attachEventListeners(digitInputs, hiddenInput)

  digitInputs[0].focus()
}

export function initOtpInputs(): void {
  const containers = document.querySelectorAll('.otp-input-container')
  containers.forEach(initOtpContainer)
}
